import { useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList } from "recharts";
import { Chip, Empty, Seal } from "@/components/brand";
import { money, fmtDate } from "@/lib/api";

export const BUREAUS = [["EQ", "Equifax", "#111111", "bg-ink"], ["TU", "TransUnion", "#B8860B", "bg-gold"], ["EX", "Experian", "#A32937", "bg-red7"]];

export function band(s) {
  if (!s) return ["—", "chip-line"];
  if (s < 580) return ["Poor", "chip-neg"];
  if (s < 670) return ["Fair", "chip-pend"];
  if (s < 740) return ["Good", "chip-ok"];
  if (s < 800) return ["Very Good", "chip-ok"];
  return ["Excellent", "chip-ok"];
}

export function Delta({ n }) {
  if (!n) return <Chip>±0</Chip>;
  return n > 0 ? <Chip tone="ok">▲ +{n}</Chip> : <Chip tone="neg">▼ {n}</Chip>;
}

export function BureauCards({ scores = {}, baseline = {}, showBand = false }) {
  return (
    <div className="grid sm:grid-cols-3 gap-4">
      {BUREAUS.map(([k, name, , bar]) => {
        const s = scores[k];
        const [b, tone] = band(s);
        const pos = s ? ((s - 300) / 550) * 100 : 0;
        return (
          <div key={k} className="bg-white border border-tanline lift" data-testid={`bureau-card-${k}`}>
            <div className={`h-[3px] ${bar}`} />
            <div className="p-6">
              <div className="flex items-center justify-between"><p className="eyebrow text-muted7">{name}</p>{showBand && <span className={`chip ${tone}`}>{b}</span>}</div>
              <div className="flex items-end gap-3 mt-3">
                <span className="f-display text-5xl font-semibold leading-none">{s ?? "—"}</span>
                {s && baseline[k] && <Delta n={s - baseline[k]} />}
              </div>
              {showBand && (
                <div className="mt-5">
                  <div className="relative h-2 bg-gradient-to-r from-[#F6E2E3] via-[#F7ECCB] to-[#E4EFE4]">
                    {s && <span className="absolute -top-1 w-1 h-4 bg-ink" style={{ left: `calc(${pos}% - 2px)` }} />}
                  </div>
                  <div className="flex justify-between f-mono text-[9px] text-muted7 mt-1.5"><span>300</span><span>850</span></div>
                  {baseline[k] && <p className="text-xs text-muted7 mt-2">Since first report: {s - baseline[k] >= 0 ? "+" : ""}{s - baseline[k]} pts</p>}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ScoreChart({ history }) {
  const [range, setRange] = useState("ALL");
  const data = useMemo(() => {
    if (range === "ALL") return history;
    const months = range === "3M" ? 3 : 6;
    const cut = new Date();
    cut.setMonth(cut.getMonth() - months);
    return history.filter((h) => new Date(h.date) >= cut);
  }, [history, range]);
  const endLabel = (key) => (props) => {
    const { x, y, index, value } = props;
    if (index !== data.length - 1) return null;
    return <text x={x + 8} y={y + 4} fontSize="11" fontFamily="JetBrains Mono" fontWeight="600" fill={BUREAUS.find((b) => b[0] === key)[2]}>{key} {value}</text>;
  };
  return (
    <div className="bg-white border border-tanline p-6" data-testid="score-chart">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="eyebrow text-muted7">Score history</p>
          <div className="flex gap-4 mt-2">{BUREAUS.map(([k, n, c]) => <span key={k} className="flex items-center gap-1.5 text-xs"><span className="w-3 h-[3px]" style={{ background: c }} />{n}</span>)}</div>
        </div>
        <div className="flex">{["3M", "6M", "ALL"].map((r) => <button key={r} onClick={() => setRange(r)} data-testid={`chart-range-${r}`} className={`btn btn-sm !min-h-[36px] ${range === r ? "btn-black" : "btn-outline"}`}>{r}</button>)}</div>
      </div>
      <div className="h-64 mt-6">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 64, left: -16, bottom: 0 }}>
            <CartesianGrid stroke="#F4F0E8" vertical={false} />
            <XAxis dataKey="date" tickFormatter={(d) => fmtDate(d, { month: "short", day: "numeric" })} tick={{ fontSize: 11, fill: "#5E584D" }} axisLine={{ stroke: "#E6DCC8" }} tickLine={false} />
            <YAxis domain={["dataMin - 20", "dataMax + 20"]} tick={{ fontSize: 11, fill: "#5E584D" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ border: "1px solid #E6DCC8", borderRadius: 0, fontSize: 12 }} labelFormatter={(d) => fmtDate(d)} />
            {BUREAUS.map(([k, n, c]) => (
              <Line key={k} type="monotone" dataKey={k} name={n} stroke={c} strokeWidth={2.5} dot={{ r: 3, fill: c }} isAnimationActive={false}>
                <LabelList dataKey={k} content={endLabel(k)} />
              </Line>
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function UtilCard({ u = {} }) {
  const total = u.total ?? 0;
  const cards = u.perCard || [];
  const target = cards.filter((c) => c.limit && c.balance / c.limit > 0.1).sort((a, b) => b.balance / b.limit - a.balance / a.limit)[0];
  const pay = target ? Math.ceil(target.balance - target.limit * 0.09) : 0;
  return (
    <div className="bg-white border border-tanline p-6" data-testid="utilization-card">
      <div className="flex items-center justify-between"><p className="eyebrow text-muted7">Utilization</p><Chip tone={total < 10 ? "ok" : "pend"}>Goal: under 10%</Chip></div>
      <p className="f-display text-5xl font-semibold mt-3">{total}%</p>
      <div className="relative h-2.5 bg-tan mt-4">
        <div className="h-full bg-gradient-to-r from-[#E8BC3E] to-[#B8860B]" style={{ width: `${Math.min(total, 100)}%` }} />
        <span className="absolute -top-1 w-px h-4.5 bg-ink" style={{ left: "10%", height: 18 }} />
      </div>
      <p className="f-mono text-[9px] text-muted7 mt-1" style={{ marginLeft: "8%" }}>10%</p>
      <p className="text-sm mt-4">{target ? <>Pay <b>{money(pay)}</b> on <b>{target.creditor}</b> to get that card under 10%.</> : total < 10 ? "You're under 10% — keep balances low before statements close." : "Keep balances low before statement dates."}</p>
      {cards.length > 0 && (
        <ul className="mt-5 space-y-3">
          {cards.slice(0, 8).map((c, i) => (
            <li key={i}>
              <div className="flex justify-between text-xs"><span className="font-medium">{c.creditor} <span className="text-muted7 f-mono">{c.maskedNumber}</span></span><span>{c.pct}% · {money(c.balance)}/{money(c.limit)}</span></div>
              <div className="h-1.5 bg-tan mt-1"><div className={`h-full ${c.pct > 30 ? "bg-red7" : c.pct > 10 ? "bg-gold" : "bg-ink"}`} style={{ width: `${Math.min(c.pct, 100)}%` }} /></div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Factors({ f = {} }) {
  const tiles = [
    ["On-time payments", f.onTime != null ? `${f.onTime}%` : "—", null],
    ["Hard inquiries", f.inquiries, f.inquiriesRemoved ? `▼ ${f.inquiriesRemoved} removed` : null],
    ["Negative items", f.negatives, f.negativesRemoved ? `▼ ${f.negativesRemoved} removed` : null],
    ["Avg. account age", f.avgAge != null ? `${f.avgAge} yrs` : "—", null],
    ["Total debt", money(f.totalDebt), null],
    ["Positive accounts", f.positive, null],
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="factor-tiles">
      {tiles.map(([l, v, s]) => (
        <div key={l} className="bg-white border border-tanline p-5">
          <p className="eyebrow text-muted7 text-[9px]">{l}</p>
          <p className="f-display text-3xl mt-2">{v ?? "—"}</p>
          {s && <p className="text-xs text-[#1F5C33] mt-1 font-semibold">{s}</p>}
        </div>
      ))}
    </div>
  );
}

function AccountsTable({ accounts = [], inquiries = [] }) {
  const [tab, setTab] = useState("All");
  const rows = tab === "Negative" ? accounts.filter((a) => a.isNegative) : tab === "Positive" ? accounts.filter((a) => !a.isNegative) : accounts;
  return (
    <div className="bg-white border border-tanline" data-testid="accounts-table">
      <div className="flex gap-6 px-6 border-b border-tanline overflow-x-auto no-scrollbar">
        {["All", "Negative", "Positive", "Inquiries"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`accounts-tab-${t.toLowerCase()}`} className={`py-4 text-sm font-medium border-b-2 -mb-px ${tab === t ? "border-gold text-ink" : "border-transparent text-muted7"}`}>
            {t} <span className="f-mono text-[10px] text-muted7">({t === "Inquiries" ? inquiries.length : t === "All" ? accounts.length : t === "Negative" ? accounts.filter((a) => a.isNegative).length : accounts.filter((a) => !a.isNegative).length})</span>
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        {tab === "Inquiries" ? (
          <table className="w-full text-sm min-w-[480px]">
            <thead><tr className="text-left f-mono text-[10px] tracking-[0.12em] uppercase text-muted7"><th className="px-6 py-3">Creditor</th><th>Date</th><th>Bureau</th><th>Status</th></tr></thead>
            <tbody>{inquiries.map((i, k) => <tr key={k} className="border-t border-tanline"><td className="px-6 py-3 font-medium">{i.creditor}</td><td>{fmtDate(i.date)}</td><td className="f-mono text-xs">{i.bureau}</td><td><Chip tone="pend">On report</Chip></td></tr>)}</tbody>
          </table>
        ) : (
          <table className="w-full text-sm min-w-[760px]">
            <thead><tr className="text-left f-mono text-[10px] tracking-[0.12em] uppercase text-muted7"><th className="px-6 py-3">Creditor</th><th>Account #</th><th>Type</th><th>Balance / limit</th><th>Opened</th><th>Bureaus</th><th className="pr-6">Status</th></tr></thead>
            <tbody>
              {rows.map((a, k) => (
                <tr key={k} className="border-t border-tanline">
                  <td className="px-6 py-3 font-medium">{a.creditor}</td>
                  <td className="f-mono text-xs">{a.maskedNumber}</td>
                  <td className="text-muted7">{a.type}</td>
                  <td>{money(a.balance)}{a.limit ? ` / ${money(a.limit)}` : ""}</td>
                  <td className="text-muted7">{a.opened ? fmtDate(a.opened, { month: "short", year: "numeric" }) : "—"}</td>
                  <td className="f-mono text-[10px]">{["EQ", "TU", "EX"].map((b) => <span key={b} className={`mr-1 ${(a.bureaus || []).includes(b) ? "text-ink" : "text-tanline"}`}>{b}</span>)}</td>
                  <td className="pr-6"><span className={`chip ${a.isNegative ? "chip-neg" : "chip-ok"}`}>{a.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function ReportView({ r }) {
  if (r.hidden) return <Empty testid="report-hidden" title="Your report is being prepared." text="I'll make it visible here as soon as it's reviewed." />;
  if (r.empty) return <Empty testid="report-empty" title="No report on file yet." text="Upload your MyFreeScoreNow report above and it fills in automatically." />;
  return (
    <div className="space-y-5" data-testid="report-view">
      <div className="bg-ink text-white p-7 sm:p-9 brackets on-dark">
        <p className="eyebrow text-gold">Your progress</p>
        <h2 className="f-display text-3xl sm:text-4xl mt-3">Up <span className="gold-foil font-semibold text-5xl sm:text-6xl" data-testid="avg-change">+{r.avgChange} pts</span> on average since you started.</h2>
        <p className="f-mono text-[10px] tracking-[0.14em] uppercase text-white/60 mt-4">Source: {r.source} · {fmtDate(r.reportDate)}</p>
      </div>
      <BureauCards scores={r.scores} baseline={r.baseline} showBand />
      {r.history?.length > 0 && <ScoreChart history={r.history} />}
      <div className="grid lg:grid-cols-[.9fr_1.1fr] gap-5">
        <UtilCard u={r.utilization} />
        <Factors f={r.factors} />
      </div>
      <AccountsTable accounts={r.accounts} inquiries={r.inquiries} />
      <div className="bg-white border border-tanline p-6" data-testid="changes-card">
        <p className="eyebrow text-muted7">What changed since last report</p>
        <ul className="mt-4 space-y-2">{(r.changes || []).map((c, i) => <li key={i} className="flex gap-3 text-sm"><span className="w-1.5 h-1.5 rotate-45 bg-red7 mt-1.5 shrink-0" />{c}</li>)}</ul>
      </div>
    </div>
  );
}

const RESULT_TONE = { Deleted: "ok", Updated: "ok", "Verified–escalate": "pend", "In review": "pend", Sent: "line", Draft: "line" };

export function UpdatesView({ items }) {
  return (
    <div className="space-y-5">
      <div className="hero-panel brackets p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
        <div>
          <h2 className="f-display text-3xl sm:text-4xl">Your verified results.</h2>
          <p className="text-muted7 mt-2">Only results Seven has verified are shown here.</p>
        </div>
        <Seal dark text="VERIFIED BY SEVEN ◆ VERIFIED BY SEVEN ◆ " size={96} />
      </div>
      {!items.length && <Empty testid="updates-empty" title="Nothing posted yet." text="Seven will post updates here once they're verified." />}
      {items.map((s) => (
        <article key={s.id} className="bg-white border border-tanline" data-testid="update-card">
          <div className="p-6 border-b border-tanline flex flex-wrap justify-between gap-3">
            <div><h3 className="f-display text-2xl">{s.name}</h3>{s.summary && <p className="text-sm text-muted7 mt-1">{s.summary}</p>}</div>
            <p className="f-mono text-[10px] tracking-[0.14em] uppercase text-muted7">{fmtDate(s.publishedAt || s.sentDate)}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[440px]">
              <thead><tr className="text-left f-mono text-[10px] tracking-[0.12em] uppercase text-muted7"><th className="px-6 py-3">Item</th><th>Bureaus</th><th className="pr-6">Result</th></tr></thead>
              <tbody>{s.items.map((i, k) => <tr key={k} className="border-t border-tanline"><td className="px-6 py-3 font-medium">{i.creditor} <span className="f-mono text-xs text-muted7">{i.maskedNumber}</span></td><td className="f-mono text-xs">{(i.bureaus || []).join(" · ")}</td><td className="pr-6"><Chip tone={RESULT_TONE[i.status] || "line"}>{i.status}</Chip></td></tr>)}</tbody>
            </table>
          </div>
        </article>
      ))}
    </div>
  );
}
